import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { Subject, finalize, takeUntil } from 'rxjs';
import { ReportsApiService, type ReportRow, type ReportSummary } from 'src/app/core/services/reports-api.service';
import { TenantOnboardingApiService } from 'src/app/core/services/tenant-onboarding-api.service';
import type { CatalogRow } from 'src/app/core/services/tenant-onboarding-api.service';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { buildReportsCsv } from './report-csv.util';
import { FilterChipKey, ReportFilterChip, buildFilterChips, periodLabel } from './report-filter-chips.util';
import { unscoredCount } from './report-outcomes.util';
import { reportDetailPath, reportDetailQueryParams } from './reports-route.util';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListModuleFilters,
  ListModulePaginationMeta,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
} from 'src/app/shared/components/list-module';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { ReportsTableComponent } from './reports-table/reports-table.component';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import { PermissionService } from 'src/app/core/services/permission.service';

export type { ReportRow };

const PAGE_SIZE = 15;

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MaterialModule,
    IconModule,
    PageSurfaceHeaderComponent,
    ListModuleSurfaceComponent,
    ReportsTableComponent,
    HasPermissionPipe,
  ],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.css',
})
export class ReportsComponent implements OnInit, OnDestroy {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly reportsApi = inject(ReportsApiService);
  private readonly tenantApi = inject(TenantOnboardingApiService);
  private readonly permissionService = inject(PermissionService);
  private readonly destroy$ = new Subject<void>();

  readonly listSurfaceConfig: ListModuleSurfaceConfig = {
    toolbar: {
      search: { placeholder: 'Search agent, client…', debounceMs: 350 },
      status: false,
      dateRange: { placeholder: 'Date range' },
    },
    pagination: true,
  };

  listFilters: ListModuleFilters = { ...DEFAULT_LIST_MODULE_FILTERS };
  selectedType = '';

  tableData: ReportRow[] = [];
  productTypes: CatalogRow[] = [];
  loading = false;
  listError = '';

  page = 1;
  readonly pageSize = PAGE_SIZE;
  meta: ListModulePaginationMeta | null = null;
  /** Totals over the whole filtered set; null against a backend that predates them. */
  summary: ReportSummary | null = null;

  /** Ignore hand-edited or malformed URL dates so they neither render as a chip nor reach the API. */
  private validYmd(value: string | null): string | null {
    return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
  }

  ngOnInit(): void {
    const q = this.route.snapshot.queryParamMap;
    const search = q.get('search');
    if (search) this.listFilters.searchQuery = search;
    const product = q.get('product_type');
    if (product) this.selectedType = product;
    const pageQ = Number(q.get('page'));
    if (pageQ > 0) this.page = pageQ;
    const from = this.validYmd(q.get('date_from'));
    const to = this.validYmd(q.get('date_to'));
    if (from) this.listFilters.dateRangeStart = from;
    if (to) this.listFilters.dateRangeEnd = to;
    if (from && to) this.listFilters.dateRange = `${from} to ${to}`;

    this.tenantApi
      .listProductTypesDropdown()
      .pipe(assertSuccessEnvelope(), takeUntil(this.destroy$))
      .subscribe({
        next: (env) => {
          this.productTypes = env.data ?? [];
        },
        error: () => {
          this.productTypes = [];
        },
      });

    if (this.canListReports()) {
      this.load();
    }
  }

  canListReports(): boolean {
    return this.permissionService.isAccessAllowed(this.P.REPORT_LIST);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    if (!this.canListReports()) {
      return;
    }
    this.loading = true;
    this.listError = '';
    const dateFrom = this.effectiveDateFrom();
    const dateTo = this.effectiveDateTo();
    this.reportsApi
      .listAgentActivity({
        search: this.listFilters.searchQuery.trim() || undefined,
        product_type: this.selectedType || undefined,
        date_from: dateFrom,
        date_to: dateTo,
        page: this.page,
        page_size: this.pageSize,
      })
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => (this.loading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (env) => {
          const d = env.data;
          if (!d) {
            this.tableData = [];
            this.meta = null;
            this.summary = null;
            return;
          }
          this.tableData = d.items ?? [];
          this.summary = d.summary ?? null;
          const total = d.total ?? 0;
          const currentPage = d.page ?? this.page;
          const perPage = d.page_size ?? this.pageSize;
          this.page = currentPage;
          this.meta = {
            total,
            currentPage,
            perPage,
            lastPage: d.last_page ?? Math.max(1, Math.ceil(total / perPage)),
          };
          this.syncFiltersToUrl();
        },
        error: (err) => {
          this.listError =
            err && typeof err === 'object' && 'message' in err && typeof (err as { message: string }).message === 'string'
              ? (err as { message: string }).message
              : 'Failed to load reports. Please try again.';
          this.tableData = [];
          this.meta = null;
          this.summary = null;
        },
      });
  }

  get filterChips(): ReportFilterChip[] {
    return buildFilterChips({
      search: this.listFilters.searchQuery,
      productType: this.selectedType,
      dateFrom: this.effectiveDateFrom(),
      dateTo: this.effectiveDateTo(),
    });
  }

  get hasActiveFilters(): boolean {
    return this.filterChips.length > 0;
  }

  get periodText(): string {
    return periodLabel(this.effectiveDateFrom(), this.effectiveDateTo());
  }

  get unscored(): number {
    return unscoredCount(this.summary);
  }

  /** Carried to each agent's report so its date range matches this list. */
  get detailQueryParams(): Record<string, string> {
    return reportDetailQueryParams({ date_from: this.effectiveDateFrom(), date_to: this.effectiveDateTo() });
  }

  /** Removes one filter, keeps the others, and goes back to page 1. */
  removeFilter(key: FilterChipKey): void {
    if (key === 'search') this.listFilters = { ...this.listFilters, searchQuery: '' };
    if (key === 'product') this.selectedType = '';
    if (key === 'dates') this.listFilters = { ...this.listFilters, dateRange: '', dateRangeStart: '', dateRangeEnd: '' };
    this.page = 1;
    this.load();
  }

  clearAllFilters(): void {
    this.listFilters = { ...DEFAULT_LIST_MODULE_FILTERS };
    this.selectedType = '';
    this.page = 1;
    this.load();
  }

  onListFiltersChange(patch: Partial<ListModuleFilters>): void {
    this.listFilters = { ...this.listFilters, ...patch };
    if (
      patch.dateRangeStart !== undefined ||
      patch.dateRangeEnd !== undefined
    ) {
      const hasStart = !!this.listFilters.dateRangeStart;
      const hasEnd = !!this.listFilters.dateRangeEnd;
      if (hasStart !== hasEnd) {
        return;
      }
    }
    this.page = 1;
    this.load();
  }

  onListPageChange(next: number): void {
    if (!this.meta || next < 1 || next > this.meta.lastPage) return;
    this.page = next;
    this.load();
  }

  onProductTypeChange(): void {
    this.page = 1;
    this.load();
  }

  private effectiveDateFrom(): string | undefined {
    return this.listFilters.dateRangeStart && this.listFilters.dateRangeEnd
      ? this.listFilters.dateRangeStart
      : undefined;
  }

  private effectiveDateTo(): string | undefined {
    return this.listFilters.dateRangeStart && this.listFilters.dateRangeEnd
      ? this.listFilters.dateRangeEnd
      : undefined;
  }

  private syncFiltersToUrl(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        search: this.listFilters.searchQuery.trim() ? this.listFilters.searchQuery.trim() : null,
        product_type: this.selectedType || null,
        date_from: this.effectiveDateFrom() ?? null,
        date_to: this.effectiveDateTo() ?? null,
        page: this.page > 1 ? this.page : null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onExport(): void {
    if (!this.permissionService.isAccessAllowed(this.P.REPORT_EXPORT)) {
      return;
    }
    if (!this.tableData.length) return;

    const blob = new Blob([buildReportsCsv(this.tableData)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agent-activity-report-${this.formatYmd(new Date())}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  onViewRow(row: ReportRow): void {
    if (!this.permissionService.isAccessAllowed(this.P.REPORT_VIEW)) {
      return;
    }
    const link = reportDetailPath(row);
    if (!link) return;
    void this.router.navigate(link, {
      queryParams: this.detailQueryParams,
    });
  }

  private formatYmd(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
